import { getCookie } from "@insite/client-framework/Common/Cookies";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { MainNavigationStyles } from "@insite/content-library/Widgets/Header/MainNavigation";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Link, { StyledIcon } from "@insite/mobius/Link";
import Menu from "@tecumseh/MobiusOverrides/Menu/Menu";
import MenuForDcProduct from "@tecumseh/Widgets/Basic/MenuForDcProduct";
import Popover from "@tecumseh/MobiusOverrides/Popover/Popover";
import { MappedLink } from "@tecumseh/Overrides/Widgets/Header/MainNavigation";
import * as React from "react";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    link: MappedLink;
    index: number;
    styles: MainNavigationStyles;
    container: React.RefObject<HTMLElement | null>;
    isOpen: boolean;
    displayChangeCustomerLink: boolean;
}

type Props = ThemeProps<BaseTheme> & OwnProps;

class MainNavigationItem extends React.Component<Props> {
    element = React.createRef<HTMLElement>();
    popover = React.createRef<any>();

    componentDidUpdate(prevProps: Readonly<Props>) {
        if (this.props.isOpen && !prevProps.isOpen) {
            this.openPopover();
        } else if (!this.props.isOpen && prevProps.isOpen) {
            this.closePopover();
        }
    }

    closePopover = () => {
        this.popover.current && (this.popover.current as any).closePopover();
    };

    openPopover = () => {
        this.popover.current && (this.popover.current as any).openPopover();
    };

    render() {
        const {
            styles,
            link,
            index,
            isOpen,
            theme: {
                breakpoints: { maxWidths },
            },
        } = this.props;

        const MegaPopover: any = Popover;
        const megaMenuStyles: any = styles.megaMenu;

        const contentBodyProps: any = {
            ...(styles.megaMenu?.contentBodyProps || {}),
            _width: 1140,
            as: "div",
        };

        const downIcon = <StyledIcon src={ChevronDown} {...styles.menuItemIcon} />;

        const hasChildren = link.children && link.children.filter(child => !child.excludeFromNavigation).length > 0;

        const isCascading =
            hasChildren && (link.childrenType === "CascadingMenu" || link?.childrenType === "CustomLinkList");

        const isMega = hasChildren && link.childrenType === "MegaMenu";

        let menuLink = (
            <Link
                className="menu-item"
                typographyProps={styles.menuItemTypography}
                color={styles.menuItemTypography?.color}
                {...styles.menuItem}
                href={link.url}
                target={link.openInNewWindow ? "_blank" : ""}
            >
                {link.title}
                {isCascading}
            </Link>
        );

        if (isMega || isCascading || link.url === "") {
            menuLink = (
                <Link
                    className="menu-item"
                    typographyProps={styles.menuItemTypography}
                    color={styles.menuItemTypography?.color}
                    href={link.url}
                    {...styles.menuItem}
                >
                    {link.title}
                    {isCascading || isMega}
                </Link>
            );
        }

        let menuItem = menuLink;

        if (isCascading && link.children) {
            const menuProps: { maxDepth?: number } = {};

            if (link.maxDepth) {
                menuProps.maxDepth = link.maxDepth;
            }

            menuItem = (
                <Menu
                    className="cascading-menu"
                    data-test-selector={`mainNavigationItem_${index}`}
                    isOpen={isOpen || undefined}
                    descriptionId={`${link.title}_${index}`}
                    menuItems={link.children}
                    menuTrigger={menuLink}
                    displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                    {...menuProps}
                    {...styles.cascadingMenu}
                />
                // link.isDcProduct ? <Menu
                //     className="cascading-menu"
                //     data-test-selector={`mainNavigationItem_${index}`}
                //     isOpen={isOpen || undefined}
                //     descriptionId={`${link.title}_${index}`}
                //     menuItems={link.children}
                //     menuTrigger={menuLink}
                //     displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                //     {...menuProps}
                //     {...styles.cascadingMenu}
                // />:<Menu
                //     className="cascading-menu"
                //     data-test-selector={`mainNavigationItem_${index}`}
                //     isOpen={isOpen || undefined}
                //     descriptionId={`${link.title}_${index}`}
                //     menuItems={link.children}
                //     menuTrigger={menuLink}
                //     displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                //     {...menuProps}
                //     {...styles.cascadingMenu}
                // />
            );
        }

        if (isMega) {
            const totalWidth = link?.children?.reduce((accumulator, o) => accumulator + Number(o.colWidth), 0);

            const flyoutContainerWidth = Number(link?.flyoutContainerWidth);

            const positionFunction = (element: React.RefObject<HTMLUListElement | null>): any => {
                if (this.props.container.current?.offsetParent) {
                    const parentPosition = this.props.container.current.getBoundingClientRect();

                    const rect = element.current?.getBoundingClientRect();

                    const top = rect ? rect.top + (rect.height > 100 ? styles.menuHeight : rect.height) : 0;

                    const cartWidth = 150;

                    const breakpointMaxWidth = maxWidths.find((value: number, index: number) => {
                        return (
                            parentPosition.width + cartWidth <= value &&
                            parentPosition.width + cartWidth > maxWidths[index - 1]
                        );
                    });

                    const width = breakpointMaxWidth ? breakpointMaxWidth - 30 : parentPosition.width;

                    return {
                        position: "absolute",
                        left: "-35px",
                        width: `${link.numberOfColumns ? flyoutContainerWidth + 30 : totalWidth && totalWidth + 30}px`,
                    };
                }

                return { position: "absolute" };
            };

            menuItem = (
                <MegaPopover
                    className="mega-menu"
                    zIndexKey="menu"
                    {...megaMenuStyles}
                    contentBodyProps={contentBodyProps}
                    controlsId={`${index}_${link.title}`}
                    ref={this.popover}
                    positionFunction={positionFunction}
                    popoverTrigger={menuLink}
                    insideRefs={[this.element]}
                >
                    <GridContainer
                        className="mega-menu-grid-container"
                        data-test-selector={`mainNavigationItem_${index}`}
                        {...styles.megaMenuGridContainer}
                        offsetProps={{
                            ...styles.megaMenuGridContainer?.offsetProps,
                            as: "ul",
                        }}
                    >
                        {link
                            .children!.filter(child => !child.excludeFromNavigation)
                            .map((child, index) => {
                                const gridItemStyle1: GridItemProps = {
                                    width: (12 / link.numberOfColumns!) as GridWidths,
                                    css: css`
                                        display: block;
                                        padding: 0px !important;
                                    `,
                                };

                                const gridItemStyle2: GridItemProps = {
                                    css: css`
                                        display: block;
                                        max-width: ${Number(child.colWidth) || 150}px !important;
                                        flex-basis: ${Number(child.colWidth) || 150}px !important;
                                    `,
                                };

                                const gridItemStyle = link?.numberOfColumns ? gridItemStyle1 : gridItemStyle2;

                                return (
                                    <GridItem className="mega-menu-grid-item" key={index} {...gridItemStyle} as="li">
                                        <Link
                                            className="mega-menu-heading-link"
                                            {...styles.megaMenuHeading}
                                            href={child.url}
                                            onClick={this.closePopover}
                                        >
                                            {child.title}
                                        </Link>

                                        {child.children && (
                                            <ul data-test-selector={`linksFor_${child.title}`}>
                                                {child.children
                                                    .filter(grandChild => {
                                                        if (grandChild.excludeFromNavigation) {
                                                            return false;
                                                        }

                                                        if (
                                                            grandChild.type === "ChangeCustomerPage" &&
                                                            !this.props.displayChangeCustomerLink
                                                        ) {
                                                            return false;
                                                        }

                                                        if (grandChild.type === "VmiUsersPage") {
                                                            const currentMode = getCookie(NavigationModeCookieName);

                                                            if (!currentMode || currentMode === "Storefront") {
                                                                return false;
                                                            }
                                                        }

                                                        return true;
                                                    })
                                                    .map((grandChild, index) => (
                                                        <li key={index}>
                                                            <Link
                                                                className="mega-menu-link"
                                                                {...styles.megaMenuLink}
                                                                onClick={this.closePopover}
                                                                href={grandChild.url}
                                                                data-test-selector={grandChild.title}
                                                            >
                                                                {grandChild.title}
                                                            </Link>
                                                        </li>
                                                    ))}
                                            </ul>
                                        )}
                                    </GridItem>
                                );
                            })}
                    </GridContainer>
                </MegaPopover>
            );
        }

        return menuItem;
    }
}

export default withTheme(MainNavigationItem);
